import React, { useState, useEffect } from 'react';
import { X, UserPlus, Mail, User as UserIcon, AlertCircle, Shield } from 'lucide-react';
import { usersService } from '../../services';
import { ValidationException } from '../../services/api';
import { User as UserType } from '../../types';
import './AddFacilityModal.css';

type UserRole = 'facility_manager' | 'consultant' | 'client';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultRole?: UserRole;
  clientId?: string;
  onSuccess?: (user: UserType) => void;
}

const ROLE_LABELS: Record<UserRole, string> = {
  facility_manager: 'Facility Manager',
  consultant: 'Consultant',
  client: 'Client',
};

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, defaultRole = 'facility_manager', clientId, onSuccess }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>(defaultRole);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRole(defaultRole);
  }, [defaultRole]);

  if (!isOpen) return null;

  const canSubmit = name.trim().length > 0 && email.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const user = await usersService.createUser({ name, email, role, client_id: clientId });
      onSuccess?.(user);
      onClose();
      setName('');
      setEmail('');
      setRole(defaultRole);
    } catch (e) {
      console.error('Failed to create user', e);
      if (e instanceof ValidationException) {
        const fieldMessage = e.errors?.[0]?.message;
        setError(fieldMessage || e.message || 'Please correct the highlighted fields.');
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Failed to create user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content add-facility-modal">
        <div className="modal-header">
          <h2>
            <UserPlus size={24} />
            Add New User
          </h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-alert">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-step">
            <div className="form-group">
              <label htmlFor="userName" className="form-label">
                <UserIcon size={16} />
                Name *
              </label>
              <input
                id="userName"
                type="text"
                className="form-input"
                placeholder="e.g., Jane Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="userEmail" className="form-label">
                <Mail size={16} />
                Email *
              </label>
              <input
                id="userEmail"
                type="email"
                className="form-input"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="userRole" className="form-label">
                <Shield size={16} />
                Role *
              </label>
              <select
                id="userRole"
                className="form-input"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
              >
                {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="footer-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !canSubmit}
            >
              {isSubmitting ? 'Creating User...' : 'Create User'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
